import {
  fetchSalesGetProspectTasks,
  useSalesGetSalesFolks,
  useSalesReassignTasks,
  type ProspectTaskDto,
} from "@crowdcoursing/api/openapi";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { useSalesBubbleId } from "~hooks/useSalesBubbleId";
import { useToast } from "~hooks/useToast";

const tReassignTitle = "Reassign open tasks?";
const tLeave = "Leave tasks";
const tReassign = "Reassign open tasks";
const tUnassign = "Unassign open tasks";
const tReassignSuccess = "Open tasks reassigned";
const tUnassignSuccess = "Open tasks unassigned";
const tReassignError = "Failed to reassign open tasks";
const tLoadError = "The owner was updated, but open tasks could not be loaded.";

const DONE: ProspectTaskDto["status"] = 4;

interface PromptRequest {
  assignedToUserId: string | null;
  assigneeName: string;
  clearing: boolean;
}

const openTaskIds = (tasks: ProspectTaskDto[] | undefined) =>
  (tasks ?? []).flatMap((task) => (task.id && task.status !== DONE ? [task.id] : []));

export const useReassignOpenTasksPrompt = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const salesBubbleId = useSalesBubbleId();
  const { data: salesFolks = [] } = useSalesGetSalesFolks({});
  const { mutateAsync: reassignTasks, isPending } = useSalesReassignTasks();
  const [request, setRequest] = useState<PromptRequest | null>(null);
  const resolver = useRef<((reassign: boolean) => void) | null>(null);

  const confirm = useCallback((next: PromptRequest) => {
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
      setRequest(next);
    });
  }, []);

  const choose = (reassign: boolean) => {
    resolver.current?.(reassign);
    resolver.current = null;
    setRequest(null);
  };

  const offerOpenTaskReassign = useCallback(
    async (prospectId: string, assignedToUserId: string | null) => {
      if (!salesBubbleId || !prospectId) {
        return;
      }

      let taskIds: string[];
      try {
        const tasks = (await fetchSalesGetProspectTasks({
          pathParams: { bubbleId: salesBubbleId, id: prospectId },
        })) as unknown as ProspectTaskDto[] | undefined;
        taskIds = openTaskIds(tasks);
      } catch {
        toast.error(tLoadError);
        return;
      }

      if (taskIds.length === 0) {
        return;
      }

      const person = salesFolks.find((user) => user.id === assignedToUserId);
      const assigneeName = [person?.firstName, person?.lastName].filter(Boolean).join(" ").trim() || "the new owner";
      const shouldReassign = await confirm({
        assignedToUserId,
        assigneeName,
        clearing: !assignedToUserId,
      });
      if (!shouldReassign) {
        return;
      }

      try {
        await reassignTasks({
          body: { assignedToUserId, taskIds },
          pathParams: { bubbleId: salesBubbleId },
        });
        await queryClient.invalidateQueries({ queryKey: ["api", "v1", "sales"] });
        toast.success(assignedToUserId ? tReassignSuccess : tUnassignSuccess);
      } catch {
        toast.error(tReassignError);
      }
    },
    [confirm, queryClient, reassignTasks, salesBubbleId, salesFolks, toast],
  );

  const message = request?.clearing
    ? "Also unassign open tasks?"
    : `Also reassign open tasks to ${request?.assigneeName ?? "the new owner"}?`;

  const reassignOpenTasksDialog = (
    <Dialog
      open={request !== null}
      onClose={() => choose(false)}
      aria-labelledby="reassign-open-tasks-title"
    >
      <DialogTitle id="reassign-open-tasks-title">{tReassignTitle}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button data-testid="leave-task-assignees" disabled={isPending} onClick={() => choose(false)}>
          {tLeave}
        </Button>
        <Button
          data-testid="reassign-open-tasks"
          disabled={isPending}
          onClick={() => choose(true)}
          variant="contained"
        >
          {request?.clearing ? tUnassign : tReassign}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { offerOpenTaskReassign, reassignOpenTasksDialog };
};
